import { AuthService, Season } from '../auth/auth.service';
import { LanguageService } from '../shared/services/language.service';
import { LogbookService } from './logbook.service';

import { DatexPipe } from '../shared/datex.pipe';

import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';

import { TranslatePipe } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import moment, { Moment } from 'moment/moment';
import { startWith } from 'rxjs';

export const MODULES = [
  CommonModule,
  MatFormFieldModule,
  MatDatepickerModule,
  ReactiveFormsModule,
  MatButtonModule,
  MatCardModule,
];
export const COMPONENTS = [FaIconComponent];
export const PIPES = [TranslatePipe, DatexPipe];

@Component({
  selector: 'app-logbook',
  templateUrl: './logbook.component.html',
  styleUrls: ['./logbook.component.scss'],
  providers: [LogbookService],
  imports: [...MODULES, ...COMPONENTS, ...PIPES],
})
export class LogbookComponent implements OnInit {
  private auth = inject(AuthService);
  private logbookService = inject(LogbookService);
  public languageService = inject(LanguageService);

  today = moment(moment().format('YYYY-MM-DD 00:00:00'));
  allowedDates = [];
  customerDocument: any;
  dateRange = new FormGroup({
    start: new FormControl<Moment>(null, Validators.required),
    end: new FormControl<Moment>(null, Validators.required),
  });

  private formStatus = toSignal(this.dateRange.statusChanges, {
    initialValue: this.dateRange.status,
  });

  dateRangeInvalid = computed(() => this.formStatus() === 'INVALID');

  private startStatusSig = toSignal(
    this.dateRange.controls.start.statusChanges.pipe(
      startWith(this.dateRange.controls.start.status)
    )
  );

  private endStatusSig = toSignal(
    this.dateRange.controls.end.statusChanges.pipe(
      startWith(this.dateRange.controls.end.status)
    )
  );

  startHasMatError = computed(() => {
    this.startStatusSig();
    return this.dateRange.controls.start.hasError('matStartDateInvalid');
  });

  endHasMatError = computed(() => {
    this.endStatusSig();
    return this.dateRange.controls.end.hasError('matEndDateInvalid');
  });

  startHasMatRequiredError = computed(() => {
    this.startStatusSig();
    return this.dateRange.controls.start.hasError('required');
  });

  endHasMatRequiredError = computed(() => {
    this.endStatusSig();
    return this.dateRange.controls.end.hasError('required');
  });

  dateFilter = (d: Moment | null): boolean => {
    return this.allowedDates.indexOf(d.unix()) >= 0;
  }; // for calendar --> only shows dates in calendar that are in allowedDates

  logbookData = signal([]);

  ngOnInit() {
    this.customerDocument = this.auth.getCustomerDocument();
    this.getDatesForCalendar();
    this.initFirstView();
  }

  getDatesForCalendar(): void {
    const dates = [];
    const customerDocument = this.auth.getCustomerDocument();
    // for every product
    for (const product of [
      'eppProducts',
      'egpProducts',
      'egpSummerProducts',
      'egwProducts',
      'egwSummerProducts',
    ]) {
      if (customerDocument.hasOwnProperty(product)) {
        const singleMainProduct = customerDocument[product]; // e.g. eopProducts
        if (this.auth.isProductActive(product, undefined, true)) {
          for (const singleProductType of singleMainProduct) {
            if (singleProductType.active) {
              const startDate = moment(
                moment(singleProductType['contractStartDtg']).format(
                  'YYYY-MM-DD 00:00:00'
                )
              );
              let endDate = this.today;

              // when product has contractExpiryDtg
              if (singleProductType['contractExpiryDtg']) {
                endDate = moment(
                  moment(singleProductType['contractExpiryDtg']).format(
                    'YYYY-MM-DD 00:00:00'
                  )
                );
                if (endDate.isAfter(this.today)) {
                  endDate = this.today;
                }
              }

              // check each day between start and expiry date
              for (
                const dtg = startDate;
                dtg.isBefore(endDate) || dtg.isSame(endDate);
                dtg.add(1, 'day')
              ) {
                const timeStamp = dtg.unix();
                if (dates.indexOf(timeStamp) < 0) {
                  // check season
                  if (this.isDateInSeason(dtg, singleProductType['saison'])) {
                    dates.push(timeStamp);
                  }
                }
              }
            }
          }
        }
      }
      this.allowedDates = dates;
    }
  }
  isDateInSeason(day: Moment, season: Season): boolean {
    if (!season) {
      return true;
    }
    // convert to moment
    const start: Moment = moment({
      year: day.year(),
      month: Number(season.start.month) - 1,
      day: Number(season.start.day),
    });
    const end: Moment = moment({
      year: day.year(),
      month: Number(season.end.month) - 1,
      day: Number(season.end.day),
    });

    if (start.isSameOrBefore(end)) {
      // start <= end, z.B. 01.04.-30.10.
      return !(day.isBefore(start) || day.isAfter(end));
    }
    // start > end, z.B. 01.11.-30.04.
    if (day.isSameOrAfter(start) || day.isSameOrBefore(end)) {
      // z.B. today = 05.12. || today = 24.03
      return true;
    }
    return false;
  }

  onQueryData(
    startDate = this.dateRange.value.start,
    endDate = this.dateRange.value.end
  ): void {
    // NOTE: one day added to dtgEnd to really get pdf till the date picked in calendar,
    //       e.g endDate picked in calendar is 24.01, without adding one day API would only deliver PDF's till 23.01
    // ----------------------------------------------------------------------
    const dtgStart = startDate.format('YYYY-MM-DDT00:00:00');
    // NOTE: 1 days added to really get the pdfs of the chosen day in calendar
    const dtgEnd = endDate.add(1, 'days').format('YYYY-MM-DDT00:00:00');

    this.getLogData(dtgStart, dtgEnd);
  }

  initFirstView() {
    const dtgEnd = this.getNewestOrMoment(moment().add(1, 'day')).format(
      'YYYY-MM-DDT00:00:00'
    );
    const dtgStart = moment(dtgEnd)
      .subtract(30, 'days')
      .format('YYYY-MM-DDT00:00:00');
    this.dateRange.controls.start.setValue(moment(dtgStart));
    this.dateRange.controls.end.setValue(moment(dtgEnd));
    this.getLogData(dtgStart, dtgEnd);
  }

  getLogData(start: string, end: string) {
    this.logbookService.fetchCustomerLogbook(start, end).subscribe(data => {
      this.logbookData.set([...data]);
    });
  }

  private getNewestOrMoment(value: Moment): Moment {
    if (!this.allowedDates?.length) {
      return value?.clone();
    }

    const maxAllowed = moment(
      Math.max(
        ...this.allowedDates.map(allowedDate =>
          allowedDate < 1e12 ? allowedDate * 1000 : allowedDate
        )
      )
    );

    // if there's any allowed date greater than the moment
    if (value.isAfter(maxAllowed)) {
      return maxAllowed;
    }

    return value;
  }
}
